import { SpinnerLoader } from "@/components/spinner-loader";
import {
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { GetEnrolleeIdModal } from "@/features/hmo-consultation/components/get-enrollee-id-modal";
import { useHMOStore } from "@/features/hmo-consultation/hooks/use-hmo-store";
import { FormSchemaType } from "@/features/hmo-consultation/schema";
import {
  GetHMOUserResponse,
  THMOUser,
} from "@/features/hmo-consultation/types";
import { addHMOUserDataToFormState } from "@/features/hmo-consultation/utils";
import { useWidgetColor } from "@/hooks/useWidgetColor";
import { identifyMixpanelUser, trackMixpanelEvent } from "@/lib/mixpanel";
import { restApi } from "@/lib/rest-api-client";
import { useQuery } from "@tanstack/react-query";
import { useRouterState } from "@tanstack/react-router";
import axios from "axios";
import { CircleCheckBig, RefreshCw } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useDebounceValue } from "usehooks-ts";

const externalSearchWhitelist = (
  import.meta.env.VITE_APP_EXTERNAL_SEARCH_WHITELIST || ""
).split(" ");

const hpID = (import.meta.env.VITE_APP_HPID || "").split(" ");

type EnrolleeIdProps = {
  setFoundUser: React.Dispatch<React.SetStateAction<boolean>>;
  name: "hmoId" | "principalHmoId";
};

export function EnrolleeId(props: EnrolleeIdProps) {
  const appState = useHMOStore();
  const widgetColor = useWidgetColor();
  const { setFoundUser, name } = props;

  const { providerId, subdomain } = appState?.partnerInfo || {};
  const routerState = useRouterState();
  // @ts-expect-error state.via is not typed
  const via = routerState?.location?.state?.via as string | undefined;
  const { getValues, control, watch, setValue, setError, clearErrors } =
    useFormContext<FormSchemaType>();

  const isDependentConsultation =
    `${getValues()?.consultationOwner}`.toLowerCase() === "dependant";

  const enrolleeType =
    isDependentConsultation && name === "hmoId"
      ? "dependant"
      : isDependentConsultation && name === "principalHmoId"
        ? "principal"
        : "enrollee";

  const isPrincipal = enrolleeType === "principal";
  const isDependant = enrolleeType === "dependant";
  const formHmoId = watch(name)?.trim();

  const isExternalSearch = externalSearchWhitelist.includes(providerId);

  const [debouncedFormHmoId] = useDebounceValue(formHmoId, 500);
  const [persistentError, setPersistentError] = useState<string | null>(null);
  const trackedHmoIdRef = useRef<string | null>(null);

  const {
    data: hmoUserById,
    isLoading: isHMOUserLoading,
    isSuccess: isHMOQuerySuccess,
    error: hmoUserError,
    refetch,
  } = useQuery({
    queryKey: [name, debouncedFormHmoId],
    queryFn: async () =>
      isExternalSearch
        ? await GET_HMO_USER_EXTERNAL(providerId!, debouncedFormHmoId!)
        : await GET_HMO_USER(providerId!, debouncedFormHmoId!),
    enabled: !!debouncedFormHmoId && debouncedFormHmoId.length >= 5,
    select: (response) => {
      if (isExternalSearch) {
        return response?.data?.data as unknown as THMOUser;
      } else {
        return response?.data?.data?.[0] as THMOUser;
      }
    },
  });

  const hmoUserData = hmoUserById;
  const foundUserForId = !!hmoUserData;
  const apiErrorMessage = (() => {
    if (!hmoUserError) return null;
    if (axios.isAxiosError(hmoUserError)) {
      if (hmoUserError.response?.data?.message)
        return hmoUserError.response.data.message;
      const status = hmoUserError.response?.status;
      if (!status)
        return "Network error. Please check your connection and try again.";
      if (status === 400)
        return "Invalid request. Please check your ID and try again.";
      if (status === 401)
        return "Authentication failed. Please refresh and try again.";
      if (status === 403) return "Access denied. Please contact your provider.";
      if (status === 404) return "Enrollee not found. Please check your ID.";
      if (status >= 500) return "Server error. Please try again.";
      return "Something went wrong. Please try again.";
    }
    return hmoUserError.message || "Something went wrong. Please try again.";
  })();

  useEffect(() => {
    if (isPrincipal) return;

    if (!hmoUserData) {
      clearErrors(name);
      setPersistentError(null);
      setFoundUser(false);
      return;
    }

    // Check if expiry date is invalid or expired
    const expiryDate = new Date(hmoUserData.expiryDate);
    const today = new Date();

    if (isNaN(expiryDate.getTime()) || expiryDate < today) {
      const errorMsg =
        "HMO plan has expired. Contact your provider for a new plan.";
      setPersistentError(errorMsg);
      setError(
        name,
        {
          type: "manual",
          message: "",
        },
        { shouldFocus: false },
      );
      setFoundUser(false);
      return;
    }

    if (hmoUserData?.deactivated) {
      const errorMsg = "Account deactivated! Contact your provider.";
      setPersistentError(errorMsg);
      setError(
        name,
        {
          type: "manual",
          message: "",
        },
        { shouldFocus: false },
      );
      setFoundUser(false);
      return;
    }

    if (hmoUserData?.allowTeleMedicine === false) {
      const errorMsg =
        "Telemedicine not allowed for this account! Contact your provider.";
      setPersistentError(errorMsg);
      setError(
        name,
        {
          type: "manual",
          message: "",
        },
        { shouldFocus: false },
      );
      setFoundUser(false);
      return;
    }

    clearErrors(name);
    setPersistentError(null);
    setFoundUser(foundUserForId);

    appState.setValues({
      userData: hmoUserData,
    });

    addHMOUserDataToFormState(setValue, hmoUserData);

    // Best-effort only — this whole component sits under an ErrorBoundary,
    // so this must never be able to crash the enrollee ID form.
    try {
      if (trackedHmoIdRef.current !== hmoUserData.hmoId) {
        trackedHmoIdRef.current = hmoUserData.hmoId;
        identifyMixpanelUser({
          hmoId: hmoUserData.hmoId,
          email: hmoUserData.email,
          firstName: hmoUserData.firstName,
          lastName: hmoUserData.lastName,
        });
        trackMixpanelEvent("hmo_id_validated", {
          via: via ?? undefined,
          source: "weblink",
          hmoId: hmoUserData.hmoId,
          partner: subdomain
            ? subdomain.charAt(0).toUpperCase() + subdomain.slice(1)
            : "",
        });
      }
    } catch (error) {
      console.warn("[Mixpanel] hmo_id_validated tracking failed", error);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hmoUserData, foundUserForId]);

  const label = isPrincipal
    ? "Principal HMO/Access ID"
    : isDependant
      ? "Dependant HMO/Access ID"
      : "HMO/Access ID";

  const placeHolder = isPrincipal
    ? "Enter Principal's ID"
    : isDependant
      ? "Enter dependant's ID"
      : "Enter your ID";

  const enrolleeInfo =
    "This is the HMO/Access ID given to you by your provider.";
  const principalInfo = "This is the ID of the Primary holder of the HMO plan.";
  const dependentInfo = "This is the ID of a beneficiary of the HMO plan.";

  const [showRetrieveIDModal, setShowRetrieveIDModal] = useState(false);

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel required className="">
            {label}
          </FormLabel>
          <FormDescription>
            {isPrincipal
              ? principalInfo
              : isDependant
                ? dependentInfo
                : enrolleeInfo}
          </FormDescription>

          <div className="flex gap-2 items-center">
            <Input
              {...field}
              onChange={(e) => field.onChange(e.target.value.trim())}
              placeholder={placeHolder}
              className="my-[2px]"
            />
            {isHMOUserLoading && <SpinnerLoader />}
            {foundUserForId && !isHMOUserLoading && (
              <CircleCheckBig className="text-green-600" />
            )}
            {hmoUserError && !isHMOUserLoading && (
              <button
                type="button"
                onClick={() => refetch()}
                title="Retry"
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            )}
          </div>

          {!foundUserForId && !isHMOUserLoading && (
            <FormDescription>
              Can't remember your access id
              <span
                style={{ color: widgetColor }}
                className="cursor-pointer"
                onClick={() => setShowRetrieveIDModal(true)}
              >
                {" "}
                click here
              </span>
              {showRetrieveIDModal && (
                <GetEnrolleeIdModal
                  open={showRetrieveIDModal}
                  onClose={() => setShowRetrieveIDModal(false)}
                />
              )}
            </FormDescription>
          )}

          {!foundUserForId && hpID.includes(providerId) && (
            <FormDescription>
              Don't have an access id,
              <a
                href="https://onelink.to/wqu28p"
                target="_blank"
                className="cursor-pointer text-primary"
              >
                {" "}
                sign up.
              </a>
            </FormDescription>
          )}

          <>
            <FormMessage />

            {persistentError && (
              <p className="text-sm font-medium text-destructive">
                {persistentError}
              </p>
            )}

            {apiErrorMessage && (
              <p className="text-sm font-medium text-destructive">
                {apiErrorMessage}
              </p>
            )}

            {!foundUserForId &&
              !isHMOUserLoading &&
              isHMOQuerySuccess &&
              !persistentError &&
              !apiErrorMessage && (
                <p className="text-sm font-medium text-destructive">
                  Invalid ID
                </p>
              )}
          </>
        </FormItem>
      )}
    />
  );
}

const GET_HMO_USER = async (providerId: string, hmoId: string) =>
  await restApi.get<GetHMOUserResponse>(
    `/enrollees?filterBy[providerId]=${encodeURIComponent(providerId)}&filterBy[hmoId]=${encodeURIComponent(hmoId.toUpperCase())}&page=1`,
  );

const GET_HMO_USER_EXTERNAL = async (providerId: string, hmoId: string) => {
  return await restApi.post<GetHMOUserResponse>(`/enrollees/search`, {
    providerId,
    hmoId: `${hmoId}`.toLowerCase(),
  });
};
